import { useEffect, useState } from 'react';

/**
 * Slider1to10 — accessible, mobile-first 1-10 selector.
 *
 * Renders 10 tick marks with the current value in a solid pill.
 * Tap-to-select or use arrow keys.
 *
 * Props:
 *   value         (number 1..10 | null)
 *   onChange      (fn(number) -> void)
 *   lowLabel      (string, left side, e.g. "Very calm")
 *   highLabel     (string, right side, e.g. "Very overwhelmed")
 *   testid        (string data-testid prefix)
 *   invertColor   (bool — true when low = good, high = bad; used for stress/anxiety)
 */
export default function Slider1to10({
  value = null,
  onChange,
  lowLabel = 'Low',
  highLabel = 'High',
  testid = 'slider',
  invertColor = false,
}) {
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (!focus) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        onChange(Math.min(10, (value ?? 5) + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        onChange(Math.max(1, (value ?? 6) - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focus, value, onChange]);

  const ticks = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div
      className="w-full"
      data-testid={testid}
      tabIndex={0}
      onFocus={()=>setFocus(true)}
      onBlur={()=>setFocus(false)}
      role="slider"
      aria-valuemin={1}
      aria-valuemax={10}
      aria-valuenow={value ?? 0}>
      <div className="flex items-baseline justify-between text-xs lg:text-sm text-navy/60 mb-3">
        <span>{lowLabel}</span>
        <span className="font-heading text-navy" data-testid={`${testid}-current`}>
          {value ?? '—'}<span className="text-navy/40 text-xs"> / 10</span>
        </span>
        <span>{highLabel}</span>
      </div>
      <div className="grid grid-cols-10 gap-1.5 lg:gap-2 select-none">
        {ticks.map((n) => {
          const isSelected = value === n;
          // Intensity color: use gold when selected on "positive" axes, navy when selected on inverted (stress/anxiety)
          const bgSelected = invertColor
            ? (n >= 8 ? 'bg-red-500 text-white' : n >= 5 ? 'bg-gold text-white' : 'bg-green text-white')
            : (n >= 8 ? 'bg-green text-white' : n >= 5 ? 'bg-gold text-white' : 'bg-navy text-white');
          return (
            <button
              key={n}
              type="button"
              onClick={()=>onChange(n)}
              data-testid={`${testid}-tick-${n}`}
              aria-label={`Set to ${n}`}
              className={`h-11 lg:h-14 rounded-2xl font-medium text-sm lg:text-base transition-smooth ${
                isSelected ? `${bgSelected} scale-105 card-soft` : 'bg-beige text-navy/60 hover:bg-[#EAE5D9]'
              }`}>
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}
